import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Users, Mail, Search, Download,
  RefreshCw, LogOut, Clock, Send,
  Shield, UserCheck, UserX, Key, Activity, Database, BarChart3, Brain
} from 'lucide-react';
import apiClient from '@/lib/api';
import { toast } from 'sonner';
import EntitiesPanel from '@/components/admin/EntitiesPanel';
import ActivityPanel from '@/components/admin/ActivityPanel';
import ApiKeysPanel from '@/components/admin/ApiKeysPanel';
import AnalyticsPanel from '@/components/admin/AnalyticsPanel';
import EditModal from '@/components/admin/EditModal';
import LoginScreen from '@/components/admin/LoginScreen';
import MemberDetailModal from '@/components/admin/MemberDetailModal';
import ApplicationDetailModal from '@/components/admin/ApplicationDetailModal';
import ApplicationsTab from '@/components/admin/ApplicationsTab';
import MembersTab from '@/components/admin/MembersTab';
import CommsTab from '@/components/admin/CommsTab';
import IntrowspectPanel from '@/components/admin/IntrowspectPanel';
import { useColumnConfig } from '@/components/admin/ColumnConfig';
import Footer from '@/components/Footer';

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [waitlist, setWaitlist] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [notices, setNotices] = useState([]);
  const [emails, setEmails] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberStats, setMemberStats] = useState(null);
  const [rejectionCodes, setRejectionCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('analytics');
  const [showNoticeForm, setShowNoticeForm] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedAppIds, setSelectedAppIds] = useState(new Set());
  const [selectedMemberIds, setSelectedMemberIds] = useState(new Set());

  // Edit modal states
  const [editingApp, setEditingApp] = useState(null);
  const [editingMember, setEditingMember] = useState(null);

  // Filter & sort states
  const [appStatusFilter, setAppStatusFilter] = useState('all');
  const [memberSearch, setMemberSearch] = useState('');
  const [appSort, setAppSort] = useState({ key: 'created_at', dir: 'desc' });
  const [memberSort, setMemberSort] = useState({ key: 'name', dir: 'asc' });
  const [appVisibleCount, setAppVisibleCount] = useState(50);
  const [memberVisibleCount, setMemberVisibleCount] = useState(50);

  // Column configs
  const APP_COLUMNS = useMemo(() => [
    { key: 'application_id', label: 'App ID' },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'profession', label: 'Profession' },
    { key: 'pan', label: 'PAN' },
    { key: 'experience_years', label: 'Exp' },
    { key: 'country', label: 'Country' },
    { key: 'status', label: 'Status' },
  ], []);

  const MEMBER_COLUMNS = useMemo(() => [
    { key: 'ainaa_id', label: 'AINAA ID' },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'profession', label: 'Profession' },
    { key: 'field', label: 'Field' },
    { key: 'trust_score', label: 'Trust' },
    { key: 'country', label: 'Country' },
    { key: 'application_id', label: 'App ID' },
  ], []);

  const appColConfig = useColumnConfig(APP_COLUMNS, 'app_table');
  const memberColConfig = useColumnConfig(MEMBER_COLUMNS, 'member_table');

  // Edit field definitions
  const APP_EDIT_FIELDS = useMemo(() => [
    { key: 'application_id', label: 'Application ID', readOnly: true },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'profession', label: 'Profession' },
    { key: 'pan', label: 'PAN / National ID' },
    { key: 'experience_years', label: 'Experience (years)', type: 'number' },
    { key: 'country', label: 'Country' },
    { key: 'improvement_area', label: 'Field / Area' },
    { key: 'imdb_link', label: 'IMDB Link' },
  ], []);

  const MEMBER_EDIT_FIELDS = useMemo(() => [
    { key: 'ainaa_id', label: 'AINAA ID', readOnly: true },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'profession', label: 'Profession' },
    { key: 'field', label: 'Field / Area' },
    { key: 'pan', label: 'PAN' },
    { key: 'aadhaar', label: 'Aadhaar' },
    { key: 'gst', label: 'GST' },
    { key: 'experience_years', label: 'Experience (years)', type: 'number' },
  ], []);

  // ── Auth ──
  useEffect(() => {
    const token = localStorage.getItem('ainaa_token');
    if (!token) { setUser(null); setChecking(false); return; }
    apiClient.get('/auth/me')
      .then(({ data }) => { setUser(data); localStorage.setItem('ainaa_admin_logged_in', 'true'); })
      .catch(() => { setUser(null); localStorage.removeItem('ainaa_admin_logged_in'); localStorage.removeItem('ainaa_token'); localStorage.removeItem('ainaa_refresh_token'); })
      .finally(() => setChecking(false));
  }, []);

  // ── Data Fetching ──
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [waitlistRes, statsRes, contactsRes, noticesRes, emailsRes, membersRes, memberStatsRes, codesRes] = await Promise.all([
        apiClient.get(`/admin/waitlist${searchTerm ? `?search=${searchTerm}` : ''}`),
        apiClient.get('/admin/waitlist/stats'),
        apiClient.get('/admin/contacts'),
        apiClient.get('/admin/notices'),
        apiClient.get('/admin/emails'),
        apiClient.get('/admin/members'),
        apiClient.get('/admin/members/stats'),
        apiClient.get('/rejection-codes'),
      ]);
      setWaitlist(waitlistRes.data);
      setStats(statsRes.data);
      setContacts(contactsRes.data);
      setNotices(noticesRes.data);
      setEmails(emailsRes.data);
      setMembers(membersRes.data);
      setMemberStats(memberStatsRes.data);
      setRejectionCodes(codesRes.data);
    } catch (error) {
      if (error.response?.status === 401) { setUser(null); return; }
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => { if (user) fetchData(); }, [user, fetchData]);

  // ── Handlers ──
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await apiClient.delete(`/admin/waitlist/${id}`);
      toast.success('Entry deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleStatusChange = async (id, status, remarks) => {
    try {
      const { data } = await apiClient.put(`/admin/waitlist/${id}/status`, { status, remarks });
      if (status === 'approved') {
        toast.success(`${data.application_id} approved — Member created in Members tab`);
      } else {
        toast.success(`Application ${data.application_id} — ${status}`);
      }
      await fetchData();
      if (status === 'approved') setActiveTab('members');
    } catch { toast.error('Failed to update status'); }
  };

  // Batch actions — applications
  const toggleAppSelect = (id) => {
    setSelectedAppIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAllApps = (allIds) => {
    setSelectedAppIds(prev => prev.size === allIds.length ? new Set() : new Set(allIds));
  };
  const batchAppAction = async (status) => {
    if (selectedAppIds.size === 0) return;
    const action = status === 'delete' ? 'delete' : status;
    if (!window.confirm(`${action.toUpperCase()} ${selectedAppIds.size} applications?`)) return;
    try {
      for (const id of selectedAppIds) {
        if (status === 'delete') {
          await apiClient.delete(`/admin/waitlist/${id}`);
        } else {
          await apiClient.put(`/admin/waitlist/${id}/status`, { status });
        }
      }
      toast.success(`${selectedAppIds.size} applications updated`);
      setSelectedAppIds(new Set());
      fetchData();
    } catch { toast.error('Batch action failed'); }
  };

  // Batch actions — members
  const toggleMemberSelect = (id) => {
    setSelectedMemberIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAllMembers = (allIds) => {
    setSelectedMemberIds(prev => prev.size === allIds.length ? new Set() : new Set(allIds));
  };
  const batchMemberDelete = async () => {
    if (selectedMemberIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedMemberIds.size} members permanently?`)) return;
    try {
      for (const id of selectedMemberIds) {
        await apiClient.delete(`/admin/members/${id}`);
      }
      toast.success(`${selectedMemberIds.size} members deleted`);
      setSelectedMemberIds(new Set());
      fetchData();
    } catch { toast.error('Batch delete failed'); }
  };

  // Notices
  const handleDeleteNotice = async (id) => {
    if (!window.confirm('Delete this notice?')) return;
    try {
      await apiClient.delete(`/admin/notices/${id}`);
      toast.success('Notice deleted');
      fetchData();
    } catch { toast.error('Failed to delete notice'); }
  };

  const handleCreateNotice = async (data) => {
    try {
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('category', data.category);
      formData.append('summary', data.summary);
      if (data.file) formData.append('file', data.file);
      await apiClient.post('/admin/notices', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Notice published');
      setShowNoticeForm(false);
      fetchData();
    } catch { toast.error('Failed to create notice'); }
  };

  const handleLogout = async () => {
    await apiClient.post('/auth/logout', {});
    localStorage.removeItem('ainaa_admin_logged_in');
    localStorage.removeItem('ainaa_token');
    localStorage.removeItem('ainaa_refresh_token');
    setUser(null);
  };

  const exportToCSV = () => {
    const headers = ['Application ID', 'Name', 'Email', 'Phone', 'Profession', 'PAN', 'Experience', 'Status', 'Remarks', 'Date'];
    const csvContent = [headers.join(','), ...waitlist.map(e => [
      e.application_id || 'N/A', `"${e.name}"`, e.email, e.phone || '', `"${e.profession}"`,
      e.pan || 'N/A', e.experience_years || '', e.status || 'pending', `"${e.remarks || ''}"`,
      new Date(e.created_at).toLocaleDateString()
    ].join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ainaa-waitlist-${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); window.URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const fmt = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  // Sort helpers
  const sortData = (data, sort) => {
    return [...data].sort((a, b) => {
      let va = a[sort.key], vb = b[sort.key];
      if (va == null) va = '';
      if (vb == null) vb = '';
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sort.dir === 'asc' ? -1 : 1;
      if (va > vb) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const toggleAppSort = (key) => {
    setAppSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };
  const toggleMemberSort = (key) => {
    setMemberSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  // Filtered & sorted data
  const filteredApps = useMemo(() => {
    let data = waitlist;
    if (appStatusFilter !== 'all') {
      data = data.filter(e => (e.status || 'verification_pending') === appStatusFilter);
    }
    return sortData(data, appSort);
  }, [waitlist, appStatusFilter, appSort]);

  const filteredMembers = useMemo(() => {
    let data = members.filter(m => m.status === 'active');
    if (memberSearch) {
      const q = memberSearch.toLowerCase();
      data = data.filter(m =>
        m.name?.toLowerCase().includes(q) ||
        m.email?.toLowerCase().includes(q) ||
        m.ainaa_id?.toLowerCase().includes(q) ||
        m.profession?.toLowerCase().includes(q)
      );
    }
    return sortData(data, memberSort);
  }, [members, memberSearch, memberSort]);

  // ── Render ──
  if (checking) return <div className="min-h-screen bg-[#EDECEA] flex items-center justify-center"><p className="text-[#9B9589] font-medium">Loading...</p></div>;
  if (!user) return <LoginScreen onLogin={setUser} />;

  const tabs = [
    { key: 'analytics', label: 'Analytics', icon: BarChart3 },
    { key: 'applications', label: `Applications (${stats?.total || 0})`, icon: Users },
    { key: 'members', label: `Members (${memberStats?.active || 0})`, icon: UserCheck },
    { key: 'entities', label: 'Entities', icon: Database },
    { key: 'introwspect', label: 'Introwspect', icon: Brain },
    { key: 'activity', label: 'Activity Log', icon: Activity },
    { key: 'apikeys', label: 'API Keys', icon: Key },
    { key: 'comms', label: 'Communications', icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col admin-console" data-testid="admin-dashboard">
      {/* Sticky Header */}
      <div className="sticky top-0 z-50 border-b-2 border-[#0F1B2D] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-[#9B9589] hover:text-[#0F1B2D] transition-colors" data-testid="admin-back-link"><ArrowLeft size={16} />Back</Link>
              <div className="h-6 w-px bg-[#D8D3CB]" />
              <span className="font-['Playfair_Display'] text-base font-semibold text-[#0F1B2D]">AINAA Admin</span>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={fetchData} className="btn-ainaa-secondary px-4 py-2 text-xs flex items-center gap-2" data-testid="refresh-btn">
                <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />Refresh
              </button>
              <button onClick={exportToCSV} className="btn-ainaa-primary px-4 py-2 text-xs flex items-center gap-2" data-testid="export-btn"><Download size={14} />Export</button>
              <button onClick={handleLogout} className="btn-ainaa-secondary px-4 py-2 text-xs flex items-center gap-2" data-testid="logout-btn"><LogOut size={14} /></button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 py-8 w-full">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          {[
            { icon: Users, label: 'Total Apps', value: stats?.total || 0, color: 'bg-[#0F1B2D]/5', iconColor: 'text-[#0F1B2D]' },
            { icon: Clock, label: 'Pending', value: stats?.pending || 0, color: 'bg-amber-50', iconColor: 'text-amber-600' },
            { icon: UserCheck, label: 'Approved', value: stats?.approved || 0, color: 'bg-emerald-50', iconColor: 'text-emerald-600' },
            { icon: UserX, label: 'Rejected', value: stats?.rejected || 0, color: 'bg-red-50', iconColor: 'text-red-600' },
            { icon: Shield, label: 'Active Members', value: memberStats?.active || 0, color: 'bg-blue-50', iconColor: 'text-blue-600' },
            { icon: Send, label: 'Emails Sent', value: emails.length, color: 'bg-purple-50', iconColor: 'text-purple-600' },
          ].map(({ icon: Icon, label, value, color, iconColor }) => (
            <div key={label} className="border border-[#D8D3CB] bg-white p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 flex items-center justify-center ${color}`}><Icon size={14} className={iconColor} /></div>
                <span className="text-[10px] font-semibold text-[#9B9589] uppercase tracking-wider">{label}</span>
              </div>
              <p className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D]">{value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-3 mb-6 flex-wrap">
          {tabs.map(({ key, label, icon: TabIcon }) => (
            <button key={key} onClick={() => setActiveTab(key)} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2 ${activeTab === key ? 'bg-[#0F1B2D] text-[#F5F3EE]' : 'border border-[#D8D3CB] text-[#9B9589] hover:border-[#0F1B2D] hover:text-[#0F1B2D]'}`} data-testid={`tab-${key}`}>
              <TabIcon size={14} />{label}
            </button>
          ))}
        </div>

        {activeTab === 'analytics' && <AnalyticsPanel />}

        {activeTab === 'applications' && (
          <ApplicationsTab
            filteredApps={filteredApps} searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            appStatusFilter={appStatusFilter} setAppStatusFilter={setAppStatusFilter}
            selectedAppIds={selectedAppIds} setSelectedAppIds={setSelectedAppIds} toggleAppSelect={toggleAppSelect} toggleAllApps={toggleAllApps} batchAppAction={batchAppAction}
            colConfig={appColConfig} appSort={appSort} toggleSort={toggleAppSort}
            appVisibleCount={appVisibleCount} setAppVisibleCount={setAppVisibleCount}
            handleStatusChange={handleStatusChange} handleDelete={handleDelete} rejectionCodes={rejectionCodes}
            setSelectedApp={setSelectedApp} setEditingApp={setEditingApp}
            isLoading={isLoading} stats={stats}
          />
        )}

        {activeTab === 'members' && (
          <MembersTab
            filteredMembers={filteredMembers} memberSearch={memberSearch} setMemberSearch={setMemberSearch}
            selectedMemberIds={selectedMemberIds} setSelectedMemberIds={setSelectedMemberIds} toggleMemberSelect={toggleMemberSelect} toggleAllMembers={toggleAllMembers} batchMemberDelete={batchMemberDelete}
            colConfig={memberColConfig} memberSort={memberSort} toggleSort={toggleMemberSort}
            memberVisibleCount={memberVisibleCount} setMemberVisibleCount={setMemberVisibleCount}
            setSelectedMember={setSelectedMember} setEditingMember={setEditingMember}
            memberStats={memberStats} isLoading={isLoading}
          />
        )}

        {activeTab === 'entities' && <EntitiesPanel />}
        {activeTab === 'introwspect' && <IntrowspectPanel />}
        {activeTab === 'activity' && <ActivityPanel />}
        {activeTab === 'apikeys' && <ApiKeysPanel />}

        {activeTab === 'comms' && (
          <CommsTab
            contacts={contacts} notices={notices} emails={emails}
            showNoticeForm={showNoticeForm} setShowNoticeForm={setShowNoticeForm}
            handleCreateNotice={handleCreateNotice} handleDeleteNotice={handleDeleteNotice}
            fmt={fmt}
          />
        )}
      </div>

      {/* Detail Modals */}
      {selectedMember && <MemberDetailModal member={selectedMember} onClose={() => setSelectedMember(null)} />}
      {selectedApp && <ApplicationDetailModal entry={selectedApp} onClose={() => setSelectedApp(null)} />}

      {/* Edit Modals */}
      <EditModal isOpen={!!editingApp} onClose={() => setEditingApp(null)} entry={editingApp} fields={APP_EDIT_FIELDS} endpoint="/admin/waitlist" idField="id" onSaved={fetchData} />
      <EditModal isOpen={!!editingMember} onClose={() => setEditingMember(null)} entry={editingMember} fields={MEMBER_EDIT_FIELDS} endpoint="/admin/members" idField="ainaa_id" onSaved={fetchData} />

      <Footer />
    </div>
  );
};

export default AdminDashboard;
